import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';

const ResetPasswordConfirm = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { post } = useApi();
    
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    
    const token = searchParams.get('token');
    const email = searchParams.get('email');

    useEffect(() => {
        if (!token || !email) {
            setError('Invalid or incomplete reset link. Please request a new recovery key.');
        }
    }, [token, email]);

        const [showPassword, setShowPassword] = useState(false);
        const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    
        const handleChange = (e) => {
            const { name, value } = e.target;
            setFormData(prev => ({ ...prev, [name]: value }));
        };
    
        const handleSubmit = async (e) => {
            e.preventDefault();
            if (formData.password !== formData.confirmPassword) {
                setError('Passwords do not match.');
                return;
            }
    
            setLoading(true);
            setMessage('');
            setError('');
    
            try {
                await post('/api/auth/password-reset/confirm/', {
                    email,
                    token,
                    new_password: formData.password
                });
                setMessage('Your password has been reset successfully. Redirecting to login...');
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } catch (err) {
                const msg = err.response?.data?.error || 'Failed to reset password. The link may have expired.';
                setError(msg);
            } finally {
                setLoading(false);
            }
        };
    
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-indigo-900/10 border border-slate-100 p-10 max-w-md w-full">
                    <div className="text-center mb-10">
                        <div className="h-16 w-16 bg-[#18216D]/5 rounded-2xl flex items-center justify-center mx-auto mb-6 text-[#18216D]">
                            <i className="fas fa-lock-open text-2xl"></i>
                        </div>
                        <h2 className="text-2xl font-black text-[#18216D]">Set New Password</h2>
                        <p className="text-slate-400 font-bold text-sm mt-2">Create a strong, secure password to access your account.</p>
                    </div>
    
                    {message && (
                        <div className="mb-6 p-4 bg-emerald-50 border border-emerald-100 text-emerald-600 rounded-2xl text-sm font-bold flex items-center gap-3">
                            <i className="fas fa-check-circle"></i>
                            {message}
                        </div>
                    )}
    
                    {error && (
                        <div className="mb-6 p-4 bg-rose-50 border border-rose-100 text-rose-600 rounded-2xl text-sm font-bold flex items-center gap-3">
                            <i className="fas fa-circle-exclamation"></i>
                            {error}
                        </div>
                    )}
    
                    {!message && (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-2 ml-1">New Password</label>
                                <div className="relative">
                                    <i className="fas fa-shield-halved absolute left-5 top-1/2 -translate-y-1/2 text-slate-300"></i>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                        disabled={loading || !!error}
                                        className="w-full pl-14 pr-12 py-4 bg-white border border-slate-300 rounded-2xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all text-sm font-medium text-[#18216D] placeholder-slate-300"
                                        placeholder="••••••••"
                                    />
                                    <button 
                                        type="button" 
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#18216D] focus:outline-none transition-colors"
                                    >
                                        <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                    </button>
                                </div>
                            </div>
    
                            <div>
                                <label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 block mb-2 ml-1">Confirm New Password</label>
                                <div className="relative">
                                    <i className="fas fa-check-double absolute left-5 top-1/2 -translate-y-1/2 text-slate-300"></i>
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        required
                                        disabled={loading || !!error}
                                        className="w-full pl-14 pr-12 py-4 bg-white border border-slate-300 rounded-2xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all text-sm font-medium text-[#18216D] placeholder-slate-300"
                                        placeholder="••••••••"
                                    />
                                    <button 
                                        type="button" 
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#18216D] focus:outline-none transition-colors"
                                    >
                                        <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                    </button>
                                </div>
                            </div>

                        <button
                            type="submit"
                            disabled={loading || !!error}
                            className={`w-full py-5 bg-[#18216D] text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-indigo-900/20 hover:bg-[#0D164F] hover:-translate-y-1 transition-all flex items-center justify-center gap-3 ${loading || !!error ? 'opacity-70 cursor-not-allowed' : ''}`}
                        >
                                {loading ? (
                                    <>
                                        <i className="fas fa-circle-notch fa-spin"></i>
                                        Updating...
                                    </>
                                ) : (
                                    <span>Set New Password</span>
                                )}
                            </button>
                    </form>
                )}

                <div className="mt-10 pt-10 border-t border-slate-50 text-center">
                    <Link to="/login" className="text-slate-400 font-bold text-sm hover:text-[#18216D] transition-colors flex items-center justify-center gap-2">
                        <i className="fas fa-arrow-left"></i>
                        Return to Sign In
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordConfirm;
